import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import { AlertController } from '@ionic/angular';
import { Observable, Subject } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { LocalStorage } from '../storage/local-storage';
import { MenuService, MenuTitle } from '../menu.service';
import { NetworkProvider } from '../network/network';
import { NewsIndispo } from './models/news-indispos.model';
import { UserService } from '../../providers/auth/user.service';

/**
 * Service qui traite les news et indispos
 */
@Injectable({ providedIn: 'root' })
export class NewsIndisposService extends LocalStorage {
  private readonly NEWS_INDISPOS_STORAGE_KEY = 'crt-news-indispos';

  /**
   * Sujet sur la mise à jour du stockage local des news&indispos
   */
  public newsIndisposSubject: Subject<void> = new Subject();

  constructor(
    override storage: Storage,
    override network: NetworkProvider,
    private httpClient: HttpClient,
    private menuService: MenuService,
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private userService: UserService
  ) {
    super();
    this.serverRequestPeriod = 10;
  }

  /**
   * Fonction qui recupere les news et indispos
   * Pas de gestion d'erreur à ce stade car la fonction sera utilisé en global dans l'appli
   */
  getNewsIndispos(forceRefresh = false, keepTraceForStatistics = true): Observable<NewsIndispo[]> {
    let profil;
    if (this.userService.isProfilLecture() && !this.userService.isProfilLectureEcriture()) {
      profil = 'CRT_LECTURE';
    }

    if (this.userService.isProfilLectureEcriture()) {
      profil = 'CRT_LECTURE_ECRITURE';
    }
    return this.shouldUseWebservice(this.NEWS_INDISPOS_STORAGE_KEY, forceRefresh).pipe(
      exhaustMap((shouldUseWebservice) => {
        if (shouldUseWebservice) {
          return this.httpClient
            .get<NewsIndispo[]>(
              `${environment.apis.API_CRT_IOT}/v1/newsindispos/mobilite?isActive=true&destination=MOBILITE&profils=${profil}`,
              {
                headers: {
                  keeptraceforstatistics: String(keepTraceForStatistics),
                },
              }
            )
            .pipe(
              switchMap((datas) => {
                this.menuService.setItemNotification(MenuTitle.newsIndispo, datas.length);
                return fromPromise(this.storage.set(this.NEWS_INDISPOS_STORAGE_KEY, datas)).pipe(
                  map(() => {
                    this.setLastRequestDateToNow(this.NEWS_INDISPOS_STORAGE_KEY);
                    this.newsIndisposSubject.next();
                    return datas;
                  })
                );
              }),
              catchError(async (err) => {
                const alert = this.alertCtrl.create({
                  header: this.translate.instant('Erreur'),
                  message: this.translate.instant('Impossible de mettre à jour les données'),
                });
                console.error(err);
                (await alert).present();
                return fromPromise(this.storage.get(this.NEWS_INDISPOS_STORAGE_KEY));
              })
            );
        } else {
          return fromPromise(this.storage.get(this.NEWS_INDISPOS_STORAGE_KEY));
        }
      })
    );
  }

  /**
   * Récupère une news ou indispo dans le stockage local via son identifiant
   */
  async getLocalNewsIndispoById(id: number): Promise<NewsIndispo> {
    const newsIndispos = await this.storage.get(this.NEWS_INDISPOS_STORAGE_KEY);
    return newsIndispos && newsIndispos.find((elmt) => elmt.identifiant === id);
  }

  /**
   * Supprime les News&Indispo du stockage local
   */
  deleteStoredNewIndispo() {
    this.storage.remove(this.NEWS_INDISPOS_STORAGE_KEY);
    this.storage.remove(this.NEWS_INDISPOS_STORAGE_KEY + this.LAST_REQUEST_DATE_SUFFIX);
    this.menuService.setItemNotification(MenuTitle.newsIndispo, 0);
  }
}
